import {
  SECTION_DEFAULT_FILL,
  compareCanvasObjectsForRender,
  isDrawableObject,
  type ArrowCorner,
  type CanvasFrame,
  type CanvasObject,
  type EmojiStampKind,
} from "@refinement-canvas/domain";
import { CANVAS_GRID_PX, CANVAS_HEIGHT_PX, CANVAS_WIDTH_PX } from "./canvas-coordinates";
import { arrowHeadPoints, computeConnectorRoute } from "./connector-geometry";
import { stripMarkdownMarkersForDisplay } from "./markdown-text";
import { resolveArrowPath } from "./shape-arrow";

export const THUMBNAIL_WIDTH = 320;
export const THUMBNAIL_HEIGHT = Math.round((CANVAS_HEIGHT_PX / CANVAS_WIDTH_PX) * THUMBNAIL_WIDTH);

export function renderCanvasThumbnailDataUrl(
  objects: readonly CanvasObject[],
  // 出力対象のキャンバス矩形（サイズプリセットに応じた原点＋寸法）。矩形外のオブジェクトはラスタ端でクリップされる。
  canvasRect: CanvasFrame
): string {
  const canvas = document.createElement("canvas");
  canvas.width = THUMBNAIL_WIDTH;
  canvas.height = THUMBNAIL_HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  const scaleX = THUMBNAIL_WIDTH / canvasRect.w;
  const scaleY = THUMBNAIL_HEIGHT / canvasRect.h;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  drawGrid(ctx, canvasRect, scaleX, scaleY);

  // コメントスレッド（ピン）はビューポート固定の UI 装飾であり、サムネイルには描画しない。
  const drawable = objects.filter((object) => isDrawableObject(object.type));
  // セクション（領域コンテナ）は常に通常オブジェクトの背面へ描画する（Web 表示のサブレイヤ分離と同じ規則）。
  const ordered = drawable.slice().sort(compareCanvasObjectsForRender);
  // コネクタは端点オブジェクトの現在位置から都度ルーティングするため id 索引を渡す。
  const objectsById = new Map(drawable.map((object) => [object.id, object]));
  for (const object of ordered) {
    ctx.save();
    // キャンバス矩形の原点分だけオフセットして描く（原点が負値の大サイズでも正しく配置される）。
    ctx.translate(
      (object.x - canvasRect.x + object.w / 2) * scaleX,
      (object.y - canvasRect.y + object.h / 2) * scaleY
    );
    ctx.rotate(((object.rotation ?? 0) * Math.PI) / 180);
    ctx.translate((-object.w / 2) * scaleX, (-object.h / 2) * scaleY);
    drawObject(ctx, object, scaleX, scaleY, objectsById);
    ctx.restore();
  }

  return canvas.toDataURL("image/png");
}

export function dataUrlToPngBlob(dataUrl: string): Blob | null {
  const [header, body] = dataUrl.split(",");
  if (!header?.startsWith("data:image/png;base64") || !body) return null;
  const binary = atob(body);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: "image/png" });
}

function drawGrid(
  ctx: CanvasRenderingContext2D,
  canvasRect: CanvasFrame,
  scaleX: number,
  scaleY: number
): void {
  // 編集画面のグリッド（canvas-grid）と同様に、キャンバス矩形原点の位相ぶんずらして、サムネイルの
  // グリッド基準位置を編集画面と揃える（大サイズで原点が負値でも一致させる）。
  const phaseX = (((canvasRect.x % CANVAS_GRID_PX) + CANVAS_GRID_PX) % CANVAS_GRID_PX) * scaleX;
  const phaseY = (((canvasRect.y % CANVAS_GRID_PX) + CANVAS_GRID_PX) % CANVAS_GRID_PX) * scaleY;
  ctx.fillStyle = "rgba(26, 26, 46, 0.08)";
  for (let x = -phaseX; x < THUMBNAIL_WIDTH; x += 24) {
    for (let y = -phaseY; y < THUMBNAIL_HEIGHT; y += 24) {
      ctx.fillRect(x, y, 1, 1);
    }
  }
}

function drawObject(
  ctx: CanvasRenderingContext2D,
  object: CanvasObject,
  scaleX: number,
  scaleY: number,
  objectsById: Map<string, CanvasObject>
): void {
  const w = Math.max(1, object.w * scaleX);
  const h = Math.max(1, object.h * scaleY);
  switch (object.type) {
    case "section": {
      // セクションは薄い塗り + 枠線 + 左上タイトルで領域を示す（Web 表示の見た目に合わせる）。
      const fill = object.fill ?? SECTION_DEFAULT_FILL;
      ctx.save();
      ctx.globalAlpha = 0.16;
      ctx.fillStyle = fill;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();
      ctx.strokeStyle = fill;
      ctx.lineWidth = 1;
      ctx.strokeRect(0, 0, w, h);
      if (object.text.length > 0) {
        ctx.fillStyle = "#1a1a2e";
        ctx.font = "7px sans-serif";
        ctx.fillText(object.text.slice(0, 24), 3, 9);
      }
      return;
    }
    case "sticky":
      ctx.fillStyle = object.color ?? "#fff5b3";
      ctx.strokeStyle = "rgba(30, 41, 59, 0.2)";
      ctx.lineWidth = 1;
      ctx.fillRect(0, 0, w, h);
      ctx.strokeRect(0, 0, w, h);
      return;
    case "shape":
      ctx.strokeStyle = object.stroke ?? "#1a1a2e";
      ctx.fillStyle = object.fill ?? "rgba(255,255,255,0)";
      ctx.lineWidth = 1.5;
      drawShape(ctx, object.shapeKind, w, h, object.arrowFromCorner);
      return;
    case "text": {
      ctx.fillStyle = object.color ?? "#1a1a2e";
      ctx.font = `${Math.max(6, (object.fontSize ?? 16) * scaleY)}px sans-serif`;
      // 文字寄せ（未指定は左寄せ）を反映し、画面表示・PDF と見た目を揃える。
      const align = object.textAlign ?? "left";
      ctx.textAlign = align;
      const anchorX = align === "center" ? w / 2 : align === "right" ? w : 0;
      // Markdown 記法のマーカー（`# ` / `- ` 等）はプレビューに出さず、本文だけを見せる
      // （リンクは画面表示と同じく表示テキストへ縮約。改行区切りは従来どおり保持）。
      ctx.fillText(
        stripMarkdownMarkersForDisplay(object.text).slice(0, 24),
        anchorX,
        Math.max(8, 14 * scaleY)
      );
      ctx.textAlign = "left";
      return;
    }
    case "image":
    case "video":
      drawMediaPlaceholder(ctx, w, h, object.type === "video" ? "#111827" : "#dbeafe");
      return;
    case "link-card":
      drawMediaPlaceholder(ctx, w, h, "#eef2ff");
      ctx.fillStyle = "#1e3a8a";
      ctx.font = "7px sans-serif";
      ctx.fillText(object.title.slice(0, 24), 4, Math.min(h - 4, 12));
      return;
    case "pen-stroke":
      ctx.strokeStyle = object.color;
      ctx.lineWidth = Math.max(1, object.thickness * scaleX);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      object.points.forEach((point, index) => {
        const x = (point.x - object.x) * scaleX;
        const y = (point.y - object.y) * scaleY;
        if (index === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
      return;
    case "stamp": {
      // サムネイルでもスタンプ位置が分かるよう、絵文字グリフを中央に描画する。
      // サムネイル生成は同期処理で画像を読み込めないため、image / video と同様にアバタースタンプも
      // プレースホルダ（人物アイコン絵文字）で位置のみ示す（小さなサムネイルでは個々のアバター画像の
      // 識別は不要）。実アバター画像の描画は、非同期プリロードを行う PDF エクスポート側で対応する。
      const glyph = object.stampKind === "avatar" ? "👤" : STAMP_THUMB_GLYPH[object.stampKind];
      const sizePx = Math.max(6, Math.min(w, h) * 0.85);
      ctx.fillStyle = "#1a1a2e";
      ctx.font = `${sizePx}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(glyph, w / 2, h / 2);
      ctx.textAlign = "start";
      ctx.textBaseline = "alphabetic";
      return;
    }
    case "connector": {
      const fromObj = objectsById.get(object.from.objectId);
      const toObj = objectsById.get(object.to.objectId);
      if (!fromObj || !toObj) return;
      const route = computeConnectorRoute(
        { x: fromObj.x, y: fromObj.y, w: fromObj.w, h: fromObj.h, rotation: fromObj.rotation ?? 0 },
        object.from.side,
        { x: toObj.x, y: toObj.y, w: toObj.w, h: toObj.h, rotation: toObj.rotation ?? 0 },
        object.to.side
      );
      if (route.length < 2) return;
      // ルートはステージ座標。ctx は object.x/object.y へ平行移動済みのため相対座標へ変換する。
      const toLocal = (p: { x: number; y: number }): { x: number; y: number } => ({
        x: (p.x - object.x) * scaleX,
        y: (p.y - object.y) * scaleY,
      });
      ctx.strokeStyle = object.stroke ?? "#475569";
      ctx.lineWidth = Math.max(1, 1.5 * Math.min(scaleX, scaleY));
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();
      route.forEach((point, index) => {
        const l = toLocal(point);
        if (index === 0) ctx.moveTo(l.x, l.y);
        else ctx.lineTo(l.x, l.y);
      });
      ctx.stroke();
      const tip = toLocal(route[route.length - 1]!);
      const before = toLocal(route[route.length - 2]!);
      const head = arrowHeadPoints(tip, before, 8 * Math.min(scaleX, scaleY));
      if (head) {
        ctx.beginPath();
        ctx.moveTo(head.left.x, head.left.y);
        ctx.lineTo(tip.x, tip.y);
        ctx.lineTo(head.right.x, head.right.y);
        ctx.stroke();
      }
      return;
    }
  }
}

// サムネイル描画用のスタンプ種別 → 絵文字グリフ対応表。
// stamp-object.tsx の STAMP_KIND_GLYPH と同じ値を持つが、循環参照回避のため複製している。
// 型は EmojiStampKind 基準にして、絵文字種別の増減にキー追従が必要になるようにする（avatar は対象外）。
const STAMP_THUMB_GLYPH: Record<EmojiStampKind, string> = {
  "thumbs-up": "👍",
  fire: "🔥",
  smile: "😊",
  hundred: "💯",
  plus: "➕",
};

function drawShape(
  ctx: CanvasRenderingContext2D,
  shapeKind: "arrow" | "circle" | "rectangle",
  w: number,
  h: number,
  arrowFromCorner?: ArrowCorner | undefined
): void {
  if (shapeKind === "circle") {
    ctx.beginPath();
    ctx.ellipse(w / 2, h / 2, Math.max(1, w / 2 - 1), Math.max(1, h / 2 - 1), 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    return;
  }
  if (shapeKind === "arrow") {
    // ドラッグ配置の矢印（arrowFromCorner 定義済み）は起点角→対角へ、未指定は従来どおり水平左→右。
    if (arrowFromCorner) {
      const path = resolveArrowPath(w, h, arrowFromCorner, 8, 4);
      ctx.beginPath();
      ctx.moveTo(path.tail.x, path.tail.y);
      ctx.lineTo(path.head.x, path.head.y);
      ctx.moveTo(path.wingA.x, path.wingA.y);
      ctx.lineTo(path.head.x, path.head.y);
      ctx.lineTo(path.wingB.x, path.wingB.y);
      ctx.stroke();
      return;
    }
    ctx.beginPath();
    ctx.moveTo(2, h / 2);
    ctx.lineTo(w - 6, h / 2);
    ctx.moveTo(w - 8, h / 2 - 4);
    ctx.lineTo(w - 2, h / 2);
    ctx.lineTo(w - 8, h / 2 + 4);
    ctx.stroke();
    return;
  }
  ctx.fillRect(0, 0, w, h);
  ctx.strokeRect(0, 0, w, h);
}

function drawMediaPlaceholder(ctx: CanvasRenderingContext2D, w: number, h: number, color: string): void {
  ctx.fillStyle = color;
  ctx.strokeStyle = "rgba(30, 41, 59, 0.2)";
  ctx.lineWidth = 1;
  ctx.fillRect(0, 0, w, h);
  ctx.strokeRect(0, 0, w, h);
}
